import {Types} from 'mongoose';
import {CrudEntityTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudEntityTypes';
import {CourseManagerAPI} from '@/classes/api/course-manager.api.class';
import {CourseStates} from '@/enums/CourseStates.enum';
import {plural} from '@/components/common/entity-picker/entityPicker.helper';
import {
    activeStudentCount,
    AVERAGE_MAX_CHILDREN_PER_COURSE,
    estimatedFreeSeats,
} from '@/components/common/entity-picker/roomContext.helper';
import {isCurrentlyInCourse} from '@/components/common/entity-picker/studentContext.helper';
import {canReadType} from '@/components/app/search/searchAccess';
import {idOf, textOf} from '@/components/app/search/searchQuickViewData';
import {
    courseBlock,
    courseRow,
    nameOf,
    QuickSchedule,
    ScheduleBlock,
    ScheduleCourse,
    ScheduleLegend,
    sectionsByWeekday,
    sortCourses,
    toneAssigner,
    weekdaysText,
} from '@/components/app/search/searchSchedule';

/** Filter field of the course service and the course field the CRUD access is checked for */
type CourseFilterKey = 'student' | 'teacher' | 'institution';

const FILTER_FIELD: Record<CourseFilterKey, string> = {
    student: 'studentItems',
    teacher: 'teacher',
    institution: 'institution',
};

const OPTIONAL_FIELDS = ['customId', 'subject', 'teacher', 'institution', 'room', 'studentItems'];

/** Populated references, each only if the user may read the referenced names */
const POPULATE: Record<string, { type: CrudEntityTypes; fields: string[] }> = {
    subject: {type: CrudEntityTypes.SUBJECT, fields: ['name']},
    institution: {type: CrudEntityTypes.INSTITUTION, fields: ['name']},
    room: {type: CrudEntityTypes.ROOM, fields: ['name']},
    teacher: {type: CrudEntityTypes.EMPLOYEE, fields: ['firstName', 'lastName']},
};

/** May the user list active courses filtered by the given key (otherwise the section is omitted) */
export function canLoadSchedule(key: CourseFilterKey): boolean {
    return canReadType(CrudEntityTypes.COURSE, 'courseState', 'endDate', 'dayOfWeek', 'timeRange', FILTER_FIELD[key]);
}

/** Fields and populate options limited to what the user may read */
export function courseQueryOptions(): { fields: string[]; populate: Record<string, { fields: string[] }> } {
    const fields = ['dayOfWeek', 'timeRange', ...OPTIONAL_FIELDS.filter((field) => canReadType(CrudEntityTypes.COURSE, field))];
    const populate: Record<string, { fields: string[] }> = {};
    for (const [field, target] of Object.entries(POPULATE)) {
        if (fields.includes(field) && canReadType(target.type, ...target.fields)) {
            populate[field] = {fields: target.fields};
        }
    }
    return {fields, populate};
}

/** One batched query: active, not ended courses matching the filter */
async function loadCourses(key: CourseFilterKey, ids: Types.ObjectId[]): Promise<ScheduleCourse[]> {
    if (!ids.length) {
        return [];
    }
    const resp = await CourseManagerAPI.find({
        filter: {[key]: ids, courseState: [CourseStates.ACTIVE], endsAfter: new Date()},
        ...courseQueryOptions(),
        skipPagination: true,
    } as any);
    return sortCourses((resp?.courses || []) as unknown as ScheduleCourse[]);
}

function blocksOf(courses: ScheduleCourse[], tone: (course: ScheduleCourse) => string, label: (course: ScheduleCourse) => string,
                  owner = ''): ScheduleBlock[] {
    return courses
        .map((course) => courseBlock(course, tone(course), label(course), owner))
        .filter((block): block is ScheduleBlock => !!block);
}

function legendOf(keys: string[], tone: (key: string) => string): ScheduleLegend[] {
    return Array.from(new Set(keys.filter((key) => key))).map((key) => ({label: key, tone: tone(key)}));
}

export type ScheduleChild = { id: string; firstName: string; name: string; isActive: boolean };

/** Courses of all children of a parent: one course query, sections per child, timetable coloured per child */
export async function loadParentSchedule(children: ScheduleChild[]): Promise<QuickSchedule | null> {
    if (!children.length || !canLoadSchedule('student')) {
        return null;
    }
    const courses = await loadCourses('student', children.filter((child) => Types.ObjectId.isValid(child.id))
        .map((child) => new Types.ObjectId(child.id)));
    const tone = toneAssigner();
    const blocks: ScheduleBlock[] = [];
    const sections = children.map((child) => {
        const childTone = tone(child.id);
        const own = courses.filter((course) => isCurrentlyInCourse(course as any, child.id));
        const label = children.length > 1 ? child.firstName || child.name : '';
        blocks.push(...blocksOf(own, () => childTone, (course) => label || textOf(course.subject), child.name));
        return {
            label: child.name,
            to: `/students/${child.id}`,
            tone: childTone,
            hint: child.isActive ? '' : 'inaktiv',
            rows: own.map((course) => courseRow(course, childTone)),
        };
    });
    const withCourses = courses.filter((course) => children.some((child) => isCurrentlyInCourse(course as any, child.id)));
    const summary = [
        plural(children.length, 'Kind', 'Kinder'),
        withCourses.length ? plural(withCourses.length, 'aktiver Kurs', 'aktive Kurse') : 'keine aktiven Kurse',
        weekdaysText(withCourses),
    ].filter((el) => el).join(' · ');
    return {
        title: 'Kurse der Kinder',
        summary,
        sections,
        blocks,
        legend: children.length > 1 ? children.map((child) => ({label: child.firstName || child.name, tone: tone(child.id)})) : [],
    };
}

/** Courses of a student, coloured per subject */
export async function loadStudentSchedule(id: Types.ObjectId): Promise<QuickSchedule | null> {
    if (!canLoadSchedule('student')) {
        return null;
    }
    const courses = (await loadCourses('student', [id])).filter((course) => isCurrentlyInCourse(course as any, String(id)));
    const tone = toneAssigner();
    const toneOf = (course: ScheduleCourse) => tone(textOf(course.subject) || 'Kurs');
    return {
        title: 'Kurse & Stundenplan',
        summary: courses.length ? [plural(courses.length, 'aktiver Kurs', 'aktive Kurse'), weekdaysText(courses)].filter((el) => el).join(' · ') : '',
        sections: [{label: '', rows: courses.map((course) => courseRow(course, toneOf(course)))}],
        blocks: blocksOf(courses, toneOf, (course) => textOf(course.subject)),
        legend: [],
    };
}

/**
 * Week of a teacher, coloured per location. Employees without teacher role and without courses get no section.
 */
export async function loadTeacherSchedule(id: Types.ObjectId, isTeacher: boolean): Promise<QuickSchedule | null> {
    if (!canLoadSchedule('teacher')) {
        return null;
    }
    const courses = await loadCourses('teacher', [id]);
    if (!courses.length && !isTeacher) {
        return null;
    }
    const tone = toneAssigner();
    const toneOf = (course: ScheduleCourse) => tone(textOf(course.institution) || idOf(course.institution) || '–');
    const institutions = new Set(courses.map((course) => idOf(course.institution) || textOf(course.institution)).filter((el) => el));
    const summary = courses.length ? [
        plural(courses.length, 'Kurs', 'Kurse'),
        institutions.size ? plural(institutions.size, 'Standort', 'Standorte') : '',
        weekdaysText(courses),
    ].filter((el) => el).join(' · ') : '';
    const rows = courses.map((course) => courseRow(course, toneOf(course), ['teacher'], true));
    const blocks = blocksOf(courses, toneOf, (course) => textOf(course.institution) || textOf(course.subject));
    return {
        title: 'Wochenplan',
        summary,
        sections: sectionsByWeekday(rows),
        blocks,
        legend: legendOf(courses.map((course) => textOf(course.institution)), tone),
    };
}

const MAX_TEACHER_TONES = 6;

/** Courses at a location per weekday with teacher, room, children and the free-seat hint (Ø 6 per course) */
export async function loadInstitutionSchedule(id: Types.ObjectId): Promise<QuickSchedule | null> {
    if (!canLoadSchedule('institution')) {
        return null;
    }
    const courses = await loadCourses('institution', [id]);
    const teachers = Array.from(new Set(courses.map((course) => textOf(course.teacher)).filter((el) => el)));
    const tone = toneAssigner();
    const colourByTeacher = teachers.length > 1 && teachers.length <= MAX_TEACHER_TONES;
    const toneOf = (course: ScheduleCourse) => (colourByTeacher ? tone(textOf(course.teacher) || '–') : 'tone-info');
    const counted = courses.filter((course) => Array.isArray(course.studentItems));
    const children = counted.reduce((sum, course) => sum + activeStudentCount(course as any), 0);
    const summary = courses.length ? [
        plural(courses.length, 'Kurs', 'Kurse'),
        counted.length ? plural(children, 'Kind', 'Kinder') : '',
        counted.length ? `noch ~${estimatedFreeSeats(counted as any)} frei (Ø ${AVERAGE_MAX_CHILDREN_PER_COURSE})` : '',
        weekdaysText(courses),
    ].filter((el) => el).join(' · ') : '';
    const rows = courses.map((course) => courseRow(course, toneOf(course), ['institution'], true));
    return {
        title: 'Kurse am Standort',
        summary,
        sections: sectionsByWeekday(rows),
        blocks: blocksOf(courses, toneOf, (course) => textOf(course.subject)),
        legend: colourByTeacher ? legendOf(teachers, tone) : [],
    };
}

const OWN_COURSE_TONE = 'tone-info';
const OTHER_COURSE_TONE = 'tone-muted';

/** Summary of a teacher week: "Name · 5 Kurse · 2 Standorte · Mo, Di" */
function teacherWeekSummary(courses: ScheduleCourse[], teacherName: string): string {
    const institutions = new Set(courses.map((course) => idOf(course.institution) || textOf(course.institution)).filter((el) => el));
    return [
        teacherName,
        plural(courses.length, 'Kurs', 'Kurse'),
        institutions.size ? plural(institutions.size, 'Standort', 'Standorte') : '',
        weekdaysText(courses),
    ].filter((el) => el).join(' · ');
}

/**
 * Week of the course's teacher: all their active courses, this course highlighted and the others muted.
 * Without teacher (or without permission to list the teacher's courses) only this course is shown.
 */
export async function loadCourseSchedule(course: ScheduleCourse, teacherId: Types.ObjectId | null): Promise<QuickSchedule> {
    const ownId = idOf(course._id) || String(course._id);
    const isOwn = (other: ScheduleCourse) => (idOf(other._id) || String(other._id)) === ownId;
    let courses: ScheduleCourse[] = [course];
    let teacherWeek = false;
    if (teacherId && canLoadSchedule('teacher')) {
        const loaded = await loadCourses('teacher', [teacherId]).catch(() => null);
        if (loaded) {
            teacherWeek = true;
            courses = sortCourses(loaded.some(isOwn) ? loaded : [...loaded, course]);
        }
    }
    const toneOf = (other: ScheduleCourse) => (isOwn(other) ? OWN_COURSE_TONE : OTHER_COURSE_TONE);
    const labelOf = (other: ScheduleCourse) => (isOwn(other) ? textOf(other.subject) : textOf(other.institution) || textOf(other.subject));
    const rows = courses.map((other) => courseRow(other, toneOf(other), ['teacher'], true));
    const hasOthers = courses.some((other) => !isOwn(other));
    return {
        title: teacherWeek ? 'Woche der Lehrkraft' : 'Kurstermin',
        summary: teacherWeek ? teacherWeekSummary(courses, nameOf(course.teacher)) : weekdaysText(courses),
        sections: sectionsByWeekday(rows),
        blocks: blocksOf(courses, toneOf, labelOf),
        legend: hasOthers ? [{label: 'Dieser Kurs', tone: OWN_COURSE_TONE}, {label: 'Weitere Kurse der Lehrkraft', tone: OTHER_COURSE_TONE}] : [],
    };
}
